import React, { useEffect, useMemo, useState } from 'react';
import { RouteComponentProps, withRouter } from 'react-router';
import { axiosKK } from '../../../services/networkRequest';
import {
  PartnerUserAccess,
  PartnerUserAccessListResponse,
} from '../../../models/APIModels';
import { useApi } from '../../../services/useApi';
import AccessTab from './AccessTab';
import PartnersListTab from './PartnersListTab';
import PricingTab from './PricingTab';
import VehiclesTab from './VehiclesTab';
import ServiceAreasTab from './ServiceAreasTab';
import { AdminNotice, PageHeader } from '../../../components/admin/Shared';

type TopTab = 'list' | 'access';
type PartnerSection = 'pricing' | 'vehicles' | 'service-areas';

interface Params {
  partnerId?: string;
}

function resolveState(pathname: string, partnerId?: string): {
  topTab: TopTab;
  selectedPartnerId: number | null;
  section: PartnerSection;
} {
  if (pathname === '/admin/partner-access') {
    return { topTab: 'access', selectedPartnerId: null, section: 'pricing' };
  }
  if (partnerId) {
    const pid = Number(partnerId);
    let section: PartnerSection = 'pricing';
    if (pathname.endsWith('/vehicles')) section = 'vehicles';
    else if (pathname.endsWith('/service-areas')) section = 'service-areas';
    return {
      topTab: 'list',
      selectedPartnerId: isNaN(pid) ? null : pid,
      section,
    };
  }
  return { topTab: 'list', selectedPartnerId: null, section: 'pricing' };
}

function PartnersPage({ history, location, match }: RouteComponentProps<Params>) {
  const { topTab, selectedPartnerId, section } = resolveState(
    location.pathname,
    match.params.partnerId
  );

  const { data, error: apiError } = useApi<PartnerUserAccessListResponse>(
    () =>
      axiosKK
        .get<PartnerUserAccessListResponse>('/logistics/admin/users/access')
        .then(({ data }) => data),
    [],
    'partners-shell-list'
  );

  const [netError, setNetError] = useState<string>();
  useEffect(() => {
    if (apiError) setNetError(apiError);
  }, [apiError]);

  const partners = useMemo(() => {
    const map = new Map<number, string>();
    (data?.users || []).forEach((u: PartnerUserAccess) => {
      if (u.partnerId && u.partnerName) {
        map.set(u.partnerId, u.partnerName);
      }
    });
    return Array.from(map, ([id, name]) => ({ id, name })).sort((a, b) =>
      a.name.localeCompare(b.name)
    );
  }, [data]);

  const selectedPartnerName =
    selectedPartnerId != null
      ? partners.find((p) => p.id === selectedPartnerId)?.name
      : undefined;

  const goList = () => history.push('/admin/partners');
  const goAccess = () => history.push('/admin/partner-access');
  const openPartner = (partnerId: number, nextSection: PartnerSection = 'pricing') =>
    history.push(`/admin/partners/${partnerId}/${nextSection}`);
  const switchSection = (nextSection: PartnerSection) => {
    if (selectedPartnerId != null) {
      history.push(`/admin/partners/${selectedPartnerId}/${nextSection}`);
    }
  };
  const renderSection = () => {
    if (!selectedPartnerId) return null;
    switch (section) {
      case 'pricing':
        return <PricingTab partnerId={selectedPartnerId} />;
      case 'vehicles':
        return <VehiclesTab partnerId={selectedPartnerId} />;
      case 'service-areas':
        return <ServiceAreasTab partnerId={selectedPartnerId} />;
    }
  };

  return (
    <>
      <PageHeader
        title="Partners"
        onBack={() => history.push('/admin/dashboard')}
      />

      <div className="admin-tab-bar">
        <button
          type="button"
          className={
            topTab === 'list'
              ? 'admin-tab-bar__tab admin-tab-bar__tab--active'
              : 'admin-tab-bar__tab'
          }
          onClick={goList}
        >
          Partners
        </button>
        <button
          type="button"
          className={
            topTab === 'access'
              ? 'admin-tab-bar__tab admin-tab-bar__tab--active'
              : 'admin-tab-bar__tab'
          }
          onClick={goAccess}
        >
          User Access
        </button>
      </div>

      <AdminNotice intent="error">{netError}</AdminNotice>

      {topTab === 'access' ? (
        <AccessTab />
      ) : selectedPartnerId != null ? (
        <section className="admin-partner-detail">
          <h4 className="admin-partner-detail__title">
            {selectedPartnerName || `Partner #${selectedPartnerId}`}
          </h4>
          <div className="admin-tab-bar admin-tab-bar--sub">
            <button
              type="button"
              className={
                section === 'pricing'
                  ? 'admin-tab-bar__tab admin-tab-bar__tab--active'
                  : 'admin-tab-bar__tab'
              }
              onClick={() => switchSection('pricing')}
            >
              Pricing
            </button>
            <button
              type="button"
              className={
                section === 'vehicles'
                  ? 'admin-tab-bar__tab admin-tab-bar__tab--active'
                  : 'admin-tab-bar__tab'
              }
              onClick={() => switchSection('vehicles')}
            >
              Vehicles
            </button>
            <button
              type="button"
              className={
                section === 'service-areas'
                  ? 'admin-tab-bar__tab admin-tab-bar__tab--active'
                  : 'admin-tab-bar__tab'
              }
              onClick={() => switchSection('service-areas')}
            >
              Service Areas
            </button>
          </div>
          {renderSection()}
        </section>
      ) : (
        <PartnersListTab partners={partners} onSelect={(id) => openPartner(id)} />
      )}
    </>
  );
}

export default withRouter(PartnersPage);
